/*
 * The list's from–to filter: which date, and the days it has to fall between.
 *
 * Sits in the right-hand corner of the list grid's toolbar rather than among the page's filters,
 * because only the list applies it. The week and the month already lay the orders out by date.
 * Which orders fall inside a range is workOrderDateFilter.js's; this only picks the range.
 */
import React from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enUS, svSE } from "@mui/x-date-pickers/locales";
import { FormattedMessage, useIntl } from "react-intl";
import messages from "../messages";
import { toDayjsLocale } from "../../cmms/cmmsDateLocale";
import { getAssetHierarchyPalette, getInputLabelSx, getMenuSx, getSelectSx } from "../../cmms/assetHierarchyStyles";
import { WorkOrderDateField, isDateRangeReversed } from "../workOrderDateFilter";

// In the grid's column order, under the columns' own headers.
const fieldOptions = [
  { value: WorkOrderDateField.PlannedStart, message: messages.fieldPlannedStart },
  { value: WorkOrderDateField.PlannedEnd, message: messages.fieldPlannedEnd },
  { value: WorkOrderDateField.Created, message: messages.fieldCreated },
];

/*
 * The pickers' own words in the page's language: the Rensa button below, and Avbryt in the
 * touch-screen dialog. No CMMS picker set these before, so the form dialogs still say Cancel in
 * Swedish.
 */
const getPickerLocaleText = (dayjsLocale) =>
  (dayjsLocale === "sv" ? svSE : enUS).components.MuiLocalizationProvider.defaultProps.localeText;

/*
 * A Rensa button in the picker, because on a touch screen it is the only way to clear one end.
 * There the field is read-only and opens a dialog, so there is no text to delete. The dialog
 * keeps its Avbryt and OK.
 */
const pickerComponentsProps = {
  actionBar: { actions: (variant) => (variant === "desktop" ? ["clear"] : ["clear", "cancel", "accept"]) },
};

const pickerWidth = 170;

export default function WorkOrderDateFilter({ value, onChange }) {
  const intl = useIntl();
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const dayjsLocale = toDayjsLocale(intl.locale);
  const isReversed = isDateRangeReversed(value);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={dayjsLocale}
      localeText={getPickerLocaleText(dayjsLocale)}
    >
      {/* Tops aligned, so the Till field's helper below it does not pull the other two down. */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="wo-date-field" sx={getInputLabelSx(cmmsColors)}>
            <FormattedMessage {...messages.dateFilterField} />
          </InputLabel>
          <Select
            labelId="wo-date-field"
            label={intl.formatMessage(messages.dateFilterField)}
            value={value.field}
            onChange={(event) => onChange({ ...value, field: event.target.value })}
            sx={getSelectSx(cmmsColors)}
            MenuProps={{ PaperProps: { sx: getMenuSx(cmmsColors) } }}
          >
            {fieldOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <FormattedMessage {...option.message} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label={intl.formatMessage(messages.dateFilterFrom)}
          value={value.from}
          onChange={(from) => onChange({ ...value, from })}
          componentsProps={pickerComponentsProps}
          renderInput={(params) => <TextField size="small" {...params} sx={{ width: pickerWidth }} />}
        />

        {/*
          * The calendar greys out the days before Från. A range can still end up reversed when
          * Från is moved past Till afterwards, and then this field says so. It is not swapped
          * round: the list stays empty until someone fixes it.
          */}
        <DatePicker
          label={intl.formatMessage(messages.dateFilterTo)}
          value={value.to}
          onChange={(to) => onChange({ ...value, to })}
          minDate={value.from}
          componentsProps={pickerComponentsProps}
          renderInput={(params) => (
            <TextField
              size="small"
              {...params}
              error={params.error || isReversed}
              helperText={isReversed ? intl.formatMessage(messages.dateFilterReversed) : undefined}
              sx={{ width: pickerWidth }}
            />
          )}
        />
      </Box>
    </LocalizationProvider>
  );
}
